/**
 * TransferButton - "Mark as Transferred" button for entity change cards.
 *
 * Disabled until all change checkboxes are checked (for UPDATED entities),
 * always enabled for CREATED/DELETED, hidden for SAME.
 * Shows a tooltip when disabled explaining what the user needs to do.
 * Supports loading spinner, transferred/skipped confirmation states,
 * and an optional skip button.
 */

import { CheckCircleIcon, MinusCircleIcon } from '@heroicons/react/20/solid';
import { ArrowPathIcon, ForwardIcon } from '@heroicons/react/24/outline';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

import type { TransferStatus } from '../utils/useTransferAction';

interface TransferButtonProps {
  enabled: boolean;
  onClick?: () => void;
  onSkip?: () => void;
  isLoading?: boolean;
  status?: TransferStatus;
  /** @deprecated Use status instead */
  isTransferred?: boolean;
}

export const TransferButton: React.FC<TransferButtonProps> = ({
  enabled,
  onClick,
  onSkip,
  isLoading,
  status: statusProp,
  isTransferred: isTransferredLegacy,
}) => {
  const resolvedStatus: TransferStatus =
    statusProp ?? (isTransferredLegacy ? 'transferred' : 'idle');

  if (resolvedStatus === 'transferred') {
    return (
      <div className="pt-2 border-t border-gray-100 mt-2">
        <div className="flex items-center justify-center gap-1.5 py-2 text-sm font-medium text-green-600">
          <CheckCircleIcon className="h-4 w-4" />
          Transferred
        </div>
      </div>
    );
  }

  if (resolvedStatus === 'skipped') {
    return (
      <div className="pt-2 border-t border-gray-100 mt-2">
        <div className="flex items-center justify-center gap-1.5 py-2 text-sm font-medium text-gray-400">
          <MinusCircleIcon className="h-4 w-4" />
          Skipped
        </div>
      </div>
    );
  }

  const transferBtn = (
    <button
      type="button"
      disabled={!enabled || isLoading}
      onClick={onClick}
      className={cn(
        'w-full text-sm font-medium py-2 px-4 rounded-md transition-colors flex items-center justify-center gap-2',
        enabled && !isLoading
          ? 'bg-blue-600 text-white hover:bg-blue-700 cursor-pointer'
          : 'bg-gray-100 text-gray-400 cursor-not-allowed',
      )}
    >
      {isLoading && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
      {isLoading ? 'Transferring\u2026' : 'Mark as Transferred'}
    </button>
  );

  const skipBtn = onSkip && (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            disabled={isLoading}
            onClick={onSkip}
            className={cn(
              'shrink-0 h-9 w-9 rounded-md border flex items-center justify-center transition-colors',
              isLoading
                ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                : 'border-gray-200 text-gray-400 hover:text-gray-600 hover:border-gray-300 cursor-pointer',
            )}
          >
            <ForwardIcon className="h-4 w-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top">
          <p>Skip this change</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  return (
    <div className="pt-2 border-t border-gray-100 mt-2">
      <div className="flex items-center gap-2">
        {enabled && !isLoading ? (
          <div className="flex-1 min-w-0">{transferBtn}</div>
        ) : (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex-1 min-w-0 block">{transferBtn}</span>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p>
                  {isLoading
                    ? 'Transfer in progress\u2026'
                    : "Check off each change to confirm you've reviewed it"}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {skipBtn}
      </div>
    </div>
  );
};
